/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Xaero Zero - xaerozero@icloud.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AuthProvider } from '../../../providers';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { CrmAlertComponent } from '../../fields';
import { UserService, BoardService } from 'src/app/services';
import { User } from 'src/app/model';
import { LogService } from 'src/app/services';


@Component({
  selector: 'page-login',
  templateUrl: 'login.page.html',
  styleUrls: ['login.page.scss']
})
export class LoginPage implements OnInit, OnDestroy {
  @ViewChild('alert') alert: CrmAlertComponent;

  loginForm: FormGroup;
  authForm: FormGroup;
  loading = false;
  returnUrl: string;
  tfaFlag: boolean = false;

  constructor(
    private fb: FormBuilder,
    private authProvider: AuthProvider,
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private boardService: BoardService,
    private logService : LogService

  ) {
    this.loginForm = this.fb.group({
      USER_EMAIL: [null, Validators.compose([Validators.required, Validators.pattern('[^ @]*@[^ @]*')])],
      USER_PASSWORD: [null, Validators.compose([Validators.required, Validators.minLength(8)])]
    });

    this.authForm = this.fb.group({
      AUTH_CODE: [null, Validators.compose([Validators.required, Validators.maxLength(6)])]
    });
  }

  onLoginSubmit(): void {
    this.alert.hide();
    localStorage.removeItem('R-CRM-TOKEN');
    localStorage.removeItem('R-CRM-PROFILE');

    if (this.loginForm.valid) {
      this.loading = true;
      const loginDetails = this.loginForm.value;
      this.authProvider.authenticate(loginDetails.USER_EMAIL, loginDetails.USER_PASSWORD)
        .subscribe(response => {
          if (response.success) {
            if (response.result.profile.TFA && !response.result.token){
              this.tfaFlag = true;
            }
            else{
              this.loginForm.reset();
              localStorage.setItem('R-CRM-TOKEN', response.result.token);
              this.userService.setUser(User.parse(response.result.profile));
              this.userService.onShowSecurityNotice.emit();
              this.boardService.boards.next(null);
              this.router.navigateByUrl(decodeURI(this.returnUrl));
            }
          } else {
            this.alert.show(response.error);
          }
           this.loading = false;
        }, err => {
          this.logService.error(err);
           this.loading = false;
        });
    }
  }

  onAuthSubmit(): void {
    this.alert.hide();
    if (this.authForm.valid) {
      this.loading = true;
      const loginDetails = this.loginForm.value;
      this.authProvider.authVerify(loginDetails.USER_EMAIL, loginDetails.USER_PASSWORD, this.authForm.value.AUTH_CODE)
        .subscribe(response => {
          if (response.success) {
            this.authForm.reset();
            this.loginForm.reset();
            localStorage.setItem('R-CRM-TOKEN', response.result.token);
            this.userService.setUser(User.parse(response.result.profile));
            this.boardService.boards.next(null);
            this.router.navigateByUrl(decodeURI(this.returnUrl));
          } else {
            this.alert.show(response.error);
          }
          this.loading = false;
        }, (error: any) => {
          this.logService.error(error);
          this.loading = false;
          this.alert.showError(error.message);
        });
    }
  }

  ngOnDestroy(): void {
  }

  ngOnInit(): void {
    this.returnUrl = this.route.snapshot.queryParams['url'] || '/';
  }

}
